import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Upload, FileText, ImageIcon, CheckCircle2, Clock, AlertTriangle, XCircle, ShieldCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { fmtCurrency } from '@/lib/formatters';
import { useToast } from '@/hooks/use-toast';
import {
  useComprovacaoBoletim,
  useEnsureFaturamento,
  useInvalidateComprovacao,
} from '@/modules/financiamento/hooks/useComprovacaoBoletim';
import {
  useNetPagamentosEvidencias,
  type NetPagamentoEvidencia,
} from '@/modules/financiamento/hooks/useNetFaturamentos';
import { calcularStatusComprovacao } from '@/modules/financiamento/lib/comprovacaoStatus';
import { EvidenciaUploadDialog } from './EvidenciaUploadDialog';

interface ComprovacaoPagamentoTabProps {
  boletimId: string;
  contratoId: string;
  /** Admin/CoAdmin/Gestor — IF/Fiscal recebe somente leitura */
  canUpload: boolean;
}

const ICONS = { check: CheckCircle2, clock: Clock, alert: AlertTriangle, x: XCircle } as const;

export function ComprovacaoPagamentoTab({
  boletimId, contratoId, canUpload,
}: ComprovacaoPagamentoTabProps) {
  const { toast } = useToast();
  const { data: comprovacao, isLoading } = useComprovacaoBoletim(boletimId);
  const ensureFaturamento = useEnsureFaturamento();
  const invalidate = useInvalidateComprovacao();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [faturamentoIdResolvido, setFaturamentoIdResolvido] = useState<string | null>(null);

  const handleAnexar = async () => {
    try {
      const id = await ensureFaturamento.mutateAsync(boletimId);
      setFaturamentoIdResolvido(id);
      setUploadOpen(true);
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao preparar comprovação',
        description: err?.message || 'Não foi possível inicializar o registro de comprovação.',
      });
    }
  };

  if (isLoading || !comprovacao) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        Carregando comprovação...
      </div>
    );
  }

  const statusVisual = calcularStatusComprovacao(
    comprovacao.cobertura_pct,
    comprovacao.dias_atraso,
  );
  const Icon = ICONS[statusVisual.icon];
  const cobertura = Math.min(100, Math.max(0, Number(comprovacao.cobertura_pct ?? 0)));

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-5 space-y-4">
          {/* Header com status combinado */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <div>
                <h3 className="font-semibold text-sm">Comprovação de Pagamento</h3>
                <p className="text-xs text-muted-foreground">
                  Evidências de recebimento vinculadas a este boletim
                </p>
              </div>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className={`${statusVisual.className} gap-1.5 px-3 py-1`}>
                  <Icon className="h-3.5 w-3.5" />
                  {statusVisual.label}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  Status derivado do valor comprovado vs. valor aprovado e do prazo de
                  comprovação ({comprovacao.sla_dias} dias após aprovação do boletim).
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Métricas principais */}
          <div className="grid grid-cols-3 gap-3 pt-1">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Aprovado</p>
              <p className="font-bold text-base">{fmtCurrency(comprovacao.valor_aprovado)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Comprovado</p>
              <p className="font-bold text-base">{fmtCurrency(comprovacao.valor_comprovado)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Cobertura</p>
              <p className="font-bold text-base">{cobertura.toFixed(0)}%</p>
            </div>
          </div>

          <Progress value={cobertura} className="h-2" />

          {/* Ação: anexar */}
          {canUpload && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleAnexar}
                disabled={ensureFaturamento.isPending}
                className="gap-1.5"
              >
                <Upload className="h-3.5 w-3.5" />
                {ensureFaturamento.isPending ? 'Preparando...' : 'Anexar comprovante'}
              </Button>
            </div>
          )}

          <Separator />

          {/* Lista de evidências */}
          <ListaEvidencias boletimId={boletimId} />
        </CardContent>
      </Card>

      {faturamentoIdResolvido && (
        <EvidenciaUploadDialog
          open={uploadOpen}
          onOpenChange={(v) => {
            setUploadOpen(v);
            if (!v) {
              setFaturamentoIdResolvido(null);
              invalidate(boletimId);
            }
          }}
          faturamentoId={faturamentoIdResolvido}
          contratoId={contratoId}
          boletimId={boletimId}
        />
      )}
    </div>
  );
}

/** Lista todas as evidências do boletim (agregando todos os faturamentos legados se houver) */
function ListaEvidencias({ boletimId }: { boletimId: string }) {
  const { data: comprovacao } = useComprovacaoBoletim(boletimId);
  // Para listar evidências, busca via todos faturamentos do boletim
  // Reusa hook existente filtrando depois — simples e suficiente para volume atual
  const faturamentoUnico = comprovacao?.total_evidencias ?? 0;

  if (!comprovacao || faturamentoUnico === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        <FileText className="h-6 w-6 mx-auto mb-1.5 opacity-40" />
        <p className="text-xs">Nenhum comprovante anexado ainda.</p>
      </div>
    );
  }

  return <EvidenciasDoBoletim boletimId={boletimId} />;
}

function EvidenciasDoBoletim({ boletimId }: { boletimId: string }) {
  // Busca evidências via JOIN: precisamos resolver faturamento_ids do boletim
  const { data: faturamentos } = useFaturamentosDoBoletim(boletimId);
  const faturamentoIds = (faturamentos ?? []).map((f) => f.id);

  if (faturamentoIds.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Comprovantes anexados
      </p>
      {faturamentoIds.map((fid) => (
        <EvidenciasPorFaturamento key={fid} faturamentoId={fid} />
      ))}
    </div>
  );
}

function EvidenciasPorFaturamento({ faturamentoId }: { faturamentoId: string }) {
  const { data: evidencias = [] } = useNetPagamentosEvidencias(faturamentoId);
  return (
    <>
      {evidencias.map((ev) => (
        <EvidenciaRow key={ev.id} evidencia={ev} />
      ))}
    </>
  );
}

function EvidenciaRow({ evidencia }: { evidencia: NetPagamentoEvidencia }) {
  const isImage = evidencia.arquivo_url?.match(/\.(jpg|jpeg|png|webp)$/i);
  const isPdf = evidencia.arquivo_url?.match(/\.pdf$/i);

  return (
    <div className="flex items-center justify-between bg-muted/30 rounded px-3 py-2 text-xs">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {isImage ? <ImageIcon className="h-3.5 w-3.5 text-primary shrink-0" /> :
         isPdf ? <FileText className="h-3.5 w-3.5 text-destructive shrink-0" /> :
         <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
        <div className="flex flex-col min-w-0">
          <span className="truncate font-medium">
            {evidencia.tipo === 'upload_comprovante' ? 'Comprovante' : 'Declaração Manual'}
          </span>
          <span className="text-muted-foreground">
            {format(new Date(evidencia.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
            {evidencia.valor_comprovado != null && ` · ${fmtCurrency(evidencia.valor_comprovado)}`}
          </span>
        </div>
      </div>
      {evidencia.arquivo_url && (
        <a
          href={evidencia.arquivo_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline shrink-0 ml-2"
        >
          Ver
        </a>
      )}
    </div>
  );
}

// Hook auxiliar local: lista faturamentos vinculados ao boletim
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
function useFaturamentosDoBoletim(boletimId: string) {
  return useQuery({
    queryKey: ['faturamentos-do-boletim', boletimId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('net_faturamentos')
        .select('id')
        .eq('boletim_id', boletimId)
        .neq('status', 'cancelado');
      if (error) throw error;
      return (data ?? []) as { id: string }[];
    },
    enabled: !!boletimId,
  });
}
